import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { twofaAPI } from '../services/api';

const accent = '#00D4AA';
const cardBg = 'rgba(255,255,255,0.03)';
const textMuted = '#94a3b8';
const danger = '#ef4444';

const NIVEAUS = [
  { key: 'uit', label: 'Uit', sub: 'Optioneel voor iedereen' },
  { key: 'beheerders', label: 'Alleen beheerders', sub: 'Verplicht voor de Beheerder-rol' },
  { key: 'iedereen', label: 'Iedereen', sub: 'Verplicht voor alle portaal-gebruikers' },
];

export default function PortalMfaSection() {
  const { gebruiker: user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [keuze, setKeuze] = useState('uit');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const isBedrijfAdmin = user && user.rol === 'bedrijf_admin';
  const isSuperAdmin = user && user.rol === 'super_admin';
  const canManage = isBedrijfAdmin || isSuperAdmin;

  useEffect(() => {
    if (!canManage || !user.bedrijf_id) { setLoading(false); return; }
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      setLoading(true); setError('');
      const res = await twofaAPI.getPortalMfa(user.bedrijf_id);
      setData(res.data);
      setKeuze(res.data.bedrijf.portal_mfa_niveau || 'uit');
    } catch (err) {
      setError(err?.response?.data?.message || 'Kon klantportaal-MFA status niet laden');
    } finally {
      setLoading(false);
    }
  }

  async function opslaan() {
    try {
      setBusy(true); setError(''); setSuccess('');
      const res = await twofaAPI.setPortalMfa(user.bedrijf_id, keuze);
      setData({ bedrijf: res.data.bedrijf, stats: res.data.stats });
      setKeuze(res.data.bedrijf.portal_mfa_niveau);
      setSuccess(res.data.message);
      setTimeout(() => setSuccess(''), 8000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  }

  if (!canManage) return null;

  if (loading) {
    return (
      <div style={{ marginTop: '24px', padding: '20px', background: cardBg, borderRadius: '12px', border: '1px solid rgba(0,212,170,0.15)' }}>
        <p style={{ color: textMuted, margin: 0 }}>Klantportaal-MFA status laden...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ marginTop: '24px', padding: '20px', background: cardBg, borderRadius: '12px', border: '1px solid ' + danger }}>
        <p style={{ color: danger, margin: 0 }}>{error}</p>
        <button type="button" onClick={loadStatus} style={{ marginTop: '12px', padding: '8px 16px', background: 'transparent', color: accent, border: '1px solid ' + accent, borderRadius: '8px', cursor: 'pointer' }}>Opnieuw proberen</button>
      </div>
    );
  }

  if (!data) return null;

  const { bedrijf, stats } = data;
  const huidig = bedrijf.portal_mfa_niveau || 'uit';
  const gewijzigd = keuze !== huidig;
  const huidigLabel = (NIVEAUS.find((n) => n.key === huidig) || NIVEAUS[0]).label;

  return (
    <div style={{ marginTop: '24px', padding: '24px', background: cardBg, borderRadius: '12px', border: '1px solid rgba(0,212,170,0.15)' }}>
      <h3 style={{ color: accent, marginTop: 0, marginBottom: '8px', fontSize: '18px' }}>
        🌐 Klantportaal-beveiliging — {bedrijf.naam}
      </h3>
      <p style={{ color: textMuted, marginTop: 0, marginBottom: '20px', fontSize: '14px' }}>
        Bepaal of klanten in het portaal van {bedrijf.naam} tweestapsverificatie verplicht moeten gebruiken. Dit staat los van de 2FA voor je eigen medewerkers hierboven.
      </p>

      <div style={{ marginBottom: '20px' }}>
        <span style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold', background: huidig === 'uit' ? 'rgba(148,163,184,0.15)' : 'rgba(0,212,170,0.15)', color: huidig === 'uit' ? textMuted : accent, border: '1px solid ' + (huidig === 'uit' ? textMuted : accent) }}>
          Huidig: {huidigLabel}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <div style={{ padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e2e8f0' }}>{stats.totaal_portal_accounts}</div>
          <div style={{ fontSize: '12px', color: textMuted }}>Portaal-accounts</div>
        </div>
        <div style={{ padding: '12px', background: 'rgba(0,212,170,0.08)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: accent }}>{stats.met_2fa}</div>
          <div style={{ fontSize: '12px', color: textMuted }}>Met 2FA actief</div>
        </div>
        <div style={{ padding: '12px', background: 'rgba(239,68,68,0.08)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: stats.zonder_2fa > 0 ? danger : textMuted }}>{stats.zonder_2fa}</div>
          <div style={{ fontSize: '12px', color: textMuted }}>Zonder 2FA</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
        {NIVEAUS.map((n) => {
          const actief = keuze === n.key;
          return (
            <button key={n.key} type="button" onClick={() => setKeuze(n.key)} style={{
              textAlign: 'left', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
              background: actief ? 'rgba(0,212,170,0.12)' : '#f8fafc',
              border: actief ? '2px solid ' + accent : '1px solid #cbd5e1',
              color: '#1e293b'
            }}>
              <div style={{ fontWeight: 'bold', fontSize: '14px', color: actief ? '#0f766e' : '#1e293b' }}>
                {actief ? '\u25CF ' : '\u25CB '}{n.label}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', marginLeft: '18px' }}>{n.sub}</div>
            </button>
          );
        })}
      </div>

      {success && (
        <div style={{ padding: '12px', background: 'rgba(0,212,170,0.1)', border: '1px solid ' + accent, borderRadius: '8px', color: accent, marginBottom: '16px', fontSize: '13px' }}>{success}</div>
      )}
      {error && (
        <div style={{ padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid ' + danger, borderRadius: '8px', color: danger, marginBottom: '16px', fontSize: '13px' }}>{error}</div>
      )}

      <button type="button" onClick={opslaan} disabled={busy || !gewijzigd} style={{
        padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold',
        border: 'none', cursor: (busy || !gewijzigd) ? 'default' : 'pointer',
        background: (busy || !gewijzigd) ? 'rgba(148,163,184,0.2)' : accent,
        color: (busy || !gewijzigd) ? textMuted : '#0a0e1a'
      }}>
        {busy ? 'Bezig...' : gewijzigd ? 'Wijziging opslaan' : 'Opgeslagen'}
      </button>
    </div>
  );
}
